import React, { useEffect, useState, useRef } from "react"
import { Autocomplete, InputAdornment, TextField } from "@mui/material"
import { debounce } from "lodash"
import Geolocator from "./Geolocator"
import apiConfig from "../../apiConfig"
import { makeApiCall } from "../../utils/gatewayUtils"

interface AddressAutocompleteProps {
  placeholderText: string
  callback: (location: string) => void
  searchingCallback?: () => void
  initText?: string
  callbackPartial?: boolean
  labelText?: string
  searchTypes?: string[]
  isRequired?: boolean
  helperText?: string
  error?: boolean
  showGeolocator?: boolean
  isDisabled?: boolean
}

type Location = {
  description: string
  placeId: string
}

type DebouncedFunction<T extends any[]> = {
  (...args: T): void
  cancel: () => void
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  placeholderText,
  callback,
  searchingCallback,
  initText = "",
  callbackPartial = true,
  labelText,
  isRequired = false,
  helperText,
  error = false,
  showGeolocator = false,
  isDisabled = false,
}) => {
  const [location, setLocation] = useState<string>(initText)
  const [suggestions, setSuggestions] = useState<Location[]>([])
  const [cachedSuggestions, setCachedSuggestions] = useState<Location[]>([])
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null)
  const debouncedFetchSuggestionsRef = useRef<DebouncedFunction<
    [string]
  > | null>(null)

  useEffect(() => {
    if (process.env.REACT_APP_ENV === "test") {
      makeApiCall(apiConfig.getAddressCache || "", { method: "GET" }).then(
        (response) => {
          setCachedSuggestions(response)
          setSuggestions(response)
        },
      )
    }

    const checkGoogleMapsLoaded = () => {
      if (window.google?.maps?.places) {
        setAutocompleteService(
          new window.google.maps.places.AutocompleteService(),
        )
      } else {
        setTimeout(checkGoogleMapsLoaded, 100)
      }
    }

    checkGoogleMapsLoaded()
  }, [])

  useEffect(() => {
    debouncedFetchSuggestionsRef.current = debounce((value: string) => {
      if (autocompleteService) {
        autocompleteService.getPlacePredictions(
          {
            input: value,
            componentRestrictions: { country: "US" },
          },
          (results, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              results
            ) {
              setSuggestions(
                results.map((result) => ({
                  description: result.description,
                  placeId: result.place_id,
                })),
              )
            }
          },
        )
      }
    }, 300)

    return () => {
      // Cleanup the debounce on component unmount
      if (debouncedFetchSuggestionsRef.current) {
        debouncedFetchSuggestionsRef.current.cancel()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autocompleteService])

  const handleInputChange = (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: string,
  ) => {
    if (reason === "input") {
      setLocation(value)
      if (value && debouncedFetchSuggestionsRef.current) {
        debouncedFetchSuggestionsRef.current(value)
      } else if (process.env.REACT_APP_ENV === "test") {
        setSuggestions(cachedSuggestions)
      } else {
        setSuggestions([])
      }
    }

    if (callbackPartial) {
      callback(value)
    }
  }

  const handleSelect = (
    event: React.ChangeEvent<{}>,
    value: Location | string | null,
  ) => {
    if (!value) {
      if (process.env.REACT_APP_ENV === "test") {
        setSuggestions(cachedSuggestions)
      } else {
        setSuggestions([])
      }
      setLocation("")
      return
    }

    const selectedDescription =
      typeof value === "string" ? value : value.description

    setLocation(selectedDescription)
    callback(selectedDescription)
    if (searchingCallback) {
      searchingCallback()
    }
  }

  return (
    <Autocomplete
      freeSolo
      value={location || ""}
      filterOptions={(x) => x}
      onChange={handleSelect}
      autoHighlight
      inputValue={location}
      onInputChange={handleInputChange}
      options={suggestions}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={labelText || placeholderText}
          placeholder={placeholderText}
          variant="outlined"
          fullWidth
          name="pickupLocation"
          margin="normal"
          required={isRequired}
          helperText={helperText}
          error={error}
          disabled={isDisabled}
          InputProps={{
            ...params.InputProps,
            startAdornment: showGeolocator && (
              <InputAdornment position="start">
                <Geolocator
                  callback={(address) => {
                    setLocation(address)
                    callback(address)
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  )
}

export default AddressAutocomplete
